import { authenticatedFetch } from '../auth-session';
import { parseFailure, publishWarnings } from '../response-feedback';
import { notifyUomsChanged } from './uoms.events';
import { getTokens } from '../auth-storage';
import { entityById } from './catalog';
import type { RecordData, RecordPage } from './types';

export class MasterdataError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public fields: Record<string, string> = {},
    public details: unknown = null
  ) {
    super(message);
  }
}
function object(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
export function asRecord(value: unknown): RecordData {
  if (!object(value)) throw new Error('The server returned an unexpected record.');
  const row: RecordData = {};
  for (const [key, item] of Object.entries(value)) {
    if (item === null || ['string', 'number', 'boolean'].includes(typeof item))
      row[key] = item as string | number | boolean | null;
  }
  return row;
}
export function asPage(value: unknown): RecordPage {
  if (value === null) return { data: [], total: 0 };
  if (Array.isArray(value)) return { data: value.map(asRecord), total: value.length };
  if (object(value) && Array.isArray(value.data))
    return {
      data: value.data.map(asRecord),
      total: typeof value.total === 'number' ? value.total : value.data.length,
    };
  return { data: [asRecord(value)], total: 1 };
}
export async function apiRequest(
  path: string,
  method = 'GET',
  data?: unknown,
  signal?: AbortSignal
): Promise<unknown> {
  const token = getTokens()?.accessToken;
  if (!token)
    throw new MasterdataError(
      'Your session has ended. Sign in again to continue.',
      401,
      'UNAUTHORIZED'
    );
  const response = await authenticatedFetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    // The API generates correlation IDs at the edge. Its CORS policy permits
    // Content-Type and Authorization, but not a client correlation-ID header.
    method,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    ...(data ? { body: JSON.stringify(data) } : {}),
    ...(signal ? { signal } : {}),
    ...(path === '/me/access' ? { cache: 'no-store' as const } : {}),
  });
  const body: unknown = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const payload = object(body) ? body : {};
    const code = typeof payload.error === 'string' ? payload.error : 'REQUEST_FAILED';
    if (path !== '/me/access' && (response.status === 403 || code === 'MAKER_CHECKER_VIOLATION'))
      window.dispatchEvent(new Event('nissei-access-refresh'));
    const failure = parseFailure(body, response.status);
    if (code === 'UOM_CODE_CONFLICT') failure.fields.code = failure.message;
    throw new MasterdataError(
      failure.message,
      response.status,
      failure.code,
      failure.fields,
      failure.payload
    );
  }
  if (method !== 'GET' && /^\/admin\/masterdata\/uoms(?:\/|$)/.test(path)) notifyUomsChanged();
  if (method !== 'GET') publishWarnings(body);
  return body;
}
export function request(
  path: string,
  method = 'GET',
  data?: RecordData,
  signal?: AbortSignal
): Promise<unknown> {
  return apiRequest(`/admin/masterdata${path}`, method, data, signal);
}
export async function readPage(
  path: string,
  query: Record<string, string> = {},
  signal?: AbortSignal
): Promise<RecordPage> {
  const params = new URLSearchParams(Object.entries(query).filter(([, value]) => value !== ''));
  try {
    return asPage(
      await request(path + (params.size ? `?${params}` : ''), 'GET', undefined, signal)
    );
  } catch (error) {
    if (
      error instanceof MasterdataError &&
      error.status === 422 &&
      /^NO_.*_FOR_DATE$/.test(error.code)
    )
      return { data: [], total: 0 };
    throw error;
  }
}
export async function referenceRecords(id: string, signal?: AbortSignal, activeOnly = false): Promise<RecordData[]> {
  const entity = entityById(id);
  const paginated = id === 'products' || entity?.paginated;
  const rows: RecordData[] = [];
  for (let page = 1; ; page++) {
    const result = await readPage(
      `/${id}`,
      { ...(paginated ? { page: String(page), pageSize: '200' } : {}), ...(id === 'factories' && activeOnly ? { active: 'true' } : {}) },
      signal
    );
    rows.push(...result.data);
    if (!paginated || rows.length >= result.total || result.data.length === 0) return rows;
  }
}
